// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 6 — Where it blows from.
//
// The question: is my bad air made here, or blown here — and from which bearing?
//
// A wind rose shows where the wind comes from. A POLLUTION rose shades each bearing and speed by
// how dirty the air was when the wind came from there, which is the only form that answers this
// question at all. The calm disc at the centre is drawn and labelled separately, because calm
// hours are exactly when local sources accumulate rather than disperse — and because Queensland
// reports no wind direction at all when the hourly average speed is zero, so calm frequency has
// to come from the SPEED column. Counting missing directions instead would attribute every
// instrument dropout to still air.

import type { Bundle } from '../data';
import { loadRose } from '../data';
import type { Rose } from '../types';
import { el, emptyState, legend, panel, segmented } from '../components/ui';
import { concentration, number, tip, UNIT } from '../format';
import { bandColour, bandLabel, BANDS, ratioColour, RATIO_COLOUR, RATIO_LABELS } from '../palette';
import { glossaryLink } from '../glossary';
import { getState, setState } from '../state';

const SECTORS = 16;
const SECTOR_NAMES = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const SPEED_RINGS = [0.5, 2, 4, 6, 10];

function ringLabel(ring: number): string {
  if (ring === 0) return `under ${SPEED_RINGS[1]} m/s`;
  if (ring >= SPEED_RINGS.length) return `over ${SPEED_RINGS[SPEED_RINGS.length - 1]} m/s`;
  return `${SPEED_RINGS[ring]}–${SPEED_RINGS[ring + 1] ?? SPEED_RINGS[SPEED_RINGS.length - 1]} m/s`;
}

// Geometry lives in src/utils/polar.ts so the layout can be tested by position rather than by
// eye — a rose that stacked every wedge at one bearing would still conserve total area.
import { polar, roseCells, wedgePath } from '../utils/polar';

type Stat = 'mean' | 'p95' | 'ratio';

/** The station's own median hour, weighted by how many hours fell in each bin. */
function stationMedian(rose: Rose): number | null {
  const points: Array<{ value: number; n: number }> = [];
  for (const cell of rose.cells) {
    if (cell) points.push({ value: cell[0], n: cell[2] });
  }
  if (rose.calm) points.push({ value: rose.calm[0], n: rose.calm[2] });
  if (!points.length) return null;
  points.sort((a, b) => a.value - b.value);
  const total = points.reduce((sum, p) => sum + p.n, 0);
  let running = 0;
  for (const point of points) {
    running += point.n;
    if (running >= total / 2) return point.value;
  }
  return points[points.length - 1].value;
}

function drawRose(
  container: HTMLElement,
  rose: Rose,
  stat: Stat,
  stationName: string,
  median: number | null
): void {
  const size = 340;
  const cx = size / 2;
  const cy = size / 2;
  const calmRadius = 26;
  const maxRadius = size / 2 - 26;
  const rings = rose.rings;

  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', `0 0 ${size} ${size}`);
  node.setAttribute('class', 'rose');
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', `Pollution rose for ${stationName}`);
  node.style.width = '100%';
  node.style.maxWidth = `${size}px`;
  node.style.height = 'auto';

  const make = (tag: string, attrs: Record<string, string | number>) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
    return element;
  };

  const geometry = roseCells(SECTORS, rings, calmRadius, maxRadius);
  for (let sector = 0; sector < SECTORS; sector++) {
    for (let ring = 0; ring < rings; ring++) {
      const cell = rose.cells[sector * rings + ring];
      const { from, inner, outer, to } = geometry[sector * rings + ring];
      const value = cell ? (stat === 'p95' ? cell[1] : cell[0]) : null;
      const path = make('path', {
        d: wedgePath(cx, cy, inner, outer, from, to),
        fill:
          value === null
            ? 'var(--missing)'
            : stat === 'ratio'
              ? ratioColour(value, median)
              : bandColour(value),
        'fill-opacity': value === null ? 0.28 : 1,
        stroke: 'var(--bg-surface)',
        'stroke-width': 0.6,
      });
      path.setAttribute(
        'data-tip',
        cell
          ? tip(
              `Wind from the ${SECTOR_NAMES[sector]}, ${ringLabel(ring)}`,
              `Mean PM2.5 ${concentration(cell[0])} ${UNIT} · 95th percentile ${concentration(cell[1])} ${UNIT}`,
              median ? `${(cell[0] / median).toFixed(2)}× this station's median hour` : null,
              `${bandLabel(value)} on the ${stat === 'p95' ? '95th percentile' : 'mean'}`,
              `${number(cell[2])} hours with this bearing and speed`
            )
          : tip(`Wind from the ${SECTOR_NAMES[sector]}, ${ringLabel(ring)}`, 'Fewer than eight hours recorded — too few to summarise.')
      );
      node.append(path);
    }
  }

  // The calm disc, drawn apart from the ramp's geometry but on the same colour scale.
  const calmValue = rose.calm ? (stat === 'p95' ? rose.calm[1] : rose.calm[0]) : null;
  const disc = make('circle', {
    cx,
    cy,
    r: calmRadius - 2,
    fill:
      calmValue === null
        ? 'var(--missing)'
        : stat === 'ratio'
          ? ratioColour(calmValue, median)
          : bandColour(calmValue),
    stroke: 'var(--text-primary)',
    'stroke-width': 1,
    'stroke-dasharray': '3 2',
  });
  disc.setAttribute(
    'data-tip',
    rose.calm
      ? tip(
          'Calm hours — wind under 0.5 m/s',
          `Mean PM2.5 ${concentration(rose.calm[0])} ${UNIT} · 95th percentile ${concentration(rose.calm[1])} ${UNIT}`,
          `${number(rose.calm[2])} calm hours`,
          'Calm frequency is derived from the wind SPEED column, because a missing direction can also mean a failed instrument.'
        )
      : 'No calm hours recorded.'
  );
  node.append(disc);
  const calmText = make('text', { x: cx, y: cy + 3, 'text-anchor': 'middle', 'font-size': 9, fill: 'var(--text-primary)' });
  calmText.textContent = 'calm';
  node.append(calmText);

  for (const [sector, label] of [[0, 'N'], [4, 'E'], [8, 'S'], [12, 'W']] as const) {
    const [x, y] = polar(cx, cy, maxRadius + 13, sector * (360 / SECTORS));
    const text = make('text', { x, y: y + 3, 'text-anchor': 'middle', 'font-size': 11, fill: 'var(--text-secondary)', 'font-weight': 600 });
    text.textContent = label;
    node.append(text);
  }

  container.append(node);
}

export async function renderRose({ bundle, container }: { bundle: Bundle; container: HTMLElement }): Promise<void> {
  const state = getState();
  const station = bundle.stations.find((candidate) => candidate.id === state.station) ?? bundle.stations[0];

  const view = panel({
    title: 'Where it blows from',
    subtitle:
      'Each wedge is a bearing the wind came from and a speed it came at, shaded by how dirty the air was in those hours. If one bearing is consistently dirty the source is upwind; if the worst air arrives in the calm disc at the centre, it is being made where you are.',
  });
  container.append(view.root);

  const withWind = bundle.stations.filter((candidate) => candidate.hasWind);
  const controls = el('div', 'control-row');

  // Compared-with-here is the DEFAULT. Australian air is clean almost everywhere almost always,
  // so on the absolute published scale a coastal city's whole rose falls in one band and the
  // chart says nothing. The absolute scales stay one click away, and the legend always names
  // which of the two is on screen.
  let stat: Stat = 'ratio';
  controls.append(
    segmented(
      [
        {
          value: 'ratio',
          label: 'Compared with here',
          tip: 'How many times this station’s own median hour each bearing runs — the question the rose exists to answer',
        },
        { value: 'mean', label: 'Average hour', tip: 'The mean PM2.5 for this bearing, on the published category scale' },
        { value: 'p95', label: 'Bad hour', tip: 'The 95th percentile — one hour in twenty from this bearing is worse' },
      ],
      stat,
      (value) => {
        stat = value as Stat;
        void draw();
      }
    )
  );
  view.body.append(controls);

  const host = el('div');
  view.body.append(host);

  let rose: Rose | null = null;

  async function draw(): Promise<void> {
    if (!rose) rose = await loadRose(station.id);
    host.innerHTML = '';
    if (!rose) {
      host.append(
        emptyState(
          `No wind is measured alongside the particles at ${station.name}.`,
          withWind.length
            ? `A pollution rose needs wind direction and speed recorded on the same mast, in the same hour, as the particle reading. ${withWind.length} stations have that. The ACT's published feed carries no wind at all, and Queensland's wind columns live in per-station files that are only fetchable for recent years.`
            : 'No station in the published set carries paired wind and particle data.'
        )
      );
      if (withWind.length) {
        const jump = el('div', 'control-row');
        for (const candidate of withWind.slice(0, 6)) {
          const button = el('button', 'button');
          button.type = 'button';
          button.textContent = `${candidate.name} (${candidate.jurisdiction})`;
          button.addEventListener('click', () => setState({ station: candidate.id }));
          jump.append(button);
        }
        host.append(jump);
      }
      return;
    }

    const median = stationMedian(rose);
    drawRose(host, rose, stat, station.name, median);

    // The legend has to name which of the two scales is on screen, or the reader is looking at
    // colours whose meaning they cannot recover.
    host.append(
      stat === 'ratio'
        ? legend(
            RATIO_LABELS.map((label, index) => ({
              label,
              hex: RATIO_COLOUR[index],
              tip: median ? `Against a median hour of ${concentration(median)} ${UNIT} at this station` : undefined,
            }))
          )
        : legend(BANDS.map((band) => ({ label: band.label, hex: band.hex })))
    );

    // Read the rose out loud: which bearing carries the dirtiest air, and does calm beat it?
    let worst: { sector: number; ring: number; value: number } | null = null;
    for (let sector = 0; sector < SECTORS; sector++) {
      for (let ring = 0; ring < rose.rings; ring++) {
        const cell = rose.cells[sector * rose.rings + ring];
        if (!cell || cell[2] < 30) continue;
        const value = stat === 'p95' ? cell[1] : cell[0];
        if (!worst || value > worst.value) worst = { sector, ring, value };
      }
    }
    const calmValue = rose.calm ? (stat === 'p95' ? rose.calm[1] : rose.calm[0]) : null;
    if (worst) {
      const calmWins = calmValue !== null && calmValue > worst.value;
      host.append(
        el(
          'p',
          'note',
          calmWins
            ? `At ${station.name} the dirtiest air arrives when the wind is <strong>not blowing at all</strong> — the calm disc reads ${concentration(
                calmValue
              )} ${UNIT} against ${concentration(worst.value)} for the worst wind bearing (${SECTOR_NAMES[worst.sector]}, ${ringLabel(
                worst.ring
              )}). That is the signature of a local source: something here is emitting, and on still hours it simply accumulates instead of dispersing. Wood heaters under a ${glossaryLink(
                'inversion',
                'winter inversion'
              )} are the usual cause.`
            : `At ${station.name} the dirtiest air arrives on <strong>${SECTOR_NAMES[worst.sector]}</strong> winds at ${ringLabel(
                worst.ring
              )}, averaging ${concentration(worst.value)} ${UNIT}${
                calmValue !== null ? `, against ${concentration(calmValue)} on calm hours` : ''
              }. A bearing that stands out like this means the source is upwind rather than underfoot — look ${SECTOR_NAMES[
                worst.sector
              ].toLowerCase()} of the monitor.`
        )
      );
    }
    host.append(
      el(
        'p',
        'note',
        `Built from ${number(rose.pairedHours)} hours in which this station measured wind and particles in the same hour. Bins with fewer than eight hours are left hatched rather than coloured in.`
      )
    );
  }

  await draw();

  view.body.append(
    el(
      'p',
      'note',
      `Direction follows the meteorological convention — the bearing the wind came FROM. Speed rings run outward from the centre. The calm disc is separated because calm is not a direction, and because ${glossaryLink(
        'pollution rose',
        'a pollution rose'
      )} that folded calm hours into an arbitrary bearing would put a local source's entire signal on one random compass point.`
    )
  );
}
